import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Car, Phone, Shield, Clock, CheckCircle, Zap,
  ArrowRight, ChevronDown, ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  exotic: 'Exotic', supercar: 'Supercar', sport: 'Sport', luxury: 'Luxury',
  suv: 'SUV', ev: 'Electric', muscle: 'Muscle', truck: 'Truck',
};

const normalizeCategory = (c) => {
  const n = (c || '').toLowerCase();
  if (n === 'sports') return 'sport';
  if (n === 'electric') return 'ev';
  return n;
};

const Rentals = () => {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showRequirements, setShowRequirements] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/rentals/fleet`).then(r => r.json()).then(d => setFleet(d.vehicles || [])).catch(() => {});
  }, []);

  const categories = ['all', ...new Set(fleet.map(v => normalizeCategory(v.category)))];
  const filtered = filter === 'all' ? fleet : fleet.filter(v => normalizeCategory(v.category) === filter);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rentals-page">

      {/* Hero */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-6">ACE Self-Drive Rentals</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6">
            Drive Something<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-amber-500">Extraordinary.</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Exclusive self-drive access to exotic, luxury, and performance vehicles. Transparent daily pricing, no hidden fees.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => { const section = document.querySelector('[data-testid="section-fleet"]'); section?.scrollIntoView({ behavior: 'smooth' }); }} className="h-12 px-6 bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="cta-browse">
              Browse the Fleet <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold" data-testid="cta-call">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>

          {/* Self-Drive vs Chauffeured comparison */}
          <div className="mt-12 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" data-testid="service-comparison">
            <div className="p-5 bg-red-600/10 border-2 border-red-600 rounded-xl text-left ring-2 ring-red-600/20">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[9px] uppercase tracking-widest font-black bg-red-600 text-white px-2 py-0.5 rounded">You're Here</span>
                <Car className="h-4 w-4 text-red-500" />
              </div>
              <p className="text-sm font-black text-white mb-1">Self-Drive Rental</p>
              <p className="text-[11px] text-gray-300 leading-relaxed">You drive. Pick up a vehicle, drive it yourself, return it. 25+ age + insurance + security deposit required.</p>
            </div>
            <button onClick={() => navigate('/rates')} className="p-5 bg-gray-900 border-2 border-gray-800 rounded-xl text-left hover:border-gray-600 transition-all cursor-pointer" data-testid="link-chauffeured">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[9px] uppercase tracking-widest font-black bg-gray-800 text-gray-300 px-2 py-0.5 rounded">Looking for a driver?</span>
                <ArrowRight className="h-4 w-4 text-gray-500" />
              </div>
              <p className="text-sm font-black text-white mb-1">Chauffeured Service</p>
              <p className="text-[11px] text-gray-400 leading-relaxed">We drive. Professional chauffeur, you ride. Hourly rates, airport transfers, events. <span className="text-red-400 underline">See rates →</span></p>
            </button>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-t border-gray-900 bg-gray-950/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap gap-2 justify-center">
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all capitalize ${
                filter === c ? 'bg-red-600 text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
              }`} data-testid={`filter-${c}`}>
              {c === 'all' ? 'All Vehicles' : CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
      </section>

      {/* Fleet Grid */}
      {filtered.length > 0 && (
        <section className="py-16 border-t border-gray-900" data-testid="section-fleet">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-8">
              <Car className="h-5 w-5 text-red-500" />
              <div>
                <h2 className="text-xl font-bold">ACE Self-Drive Fleet</h2>
                <p className="text-xs text-gray-500">Published daily rates</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((v, i) => (
                <Card key={v.rental_id || i}
                  className="bg-gray-950 border-gray-800 overflow-hidden group hover:border-gray-600 transition-all cursor-pointer"
                  onClick={() => navigate(`/rentals/${v.rental_id}`)}
                  data-testid={`rental-card-${v.rental_id}`}>
                  {/* Thumbnail */}
                  <div className="relative h-44 overflow-hidden bg-gray-900">
                    <img
                      src={v.thumbnail_url}
                      alt={v.vehicle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2.5 py-1 rounded-full uppercase tracking-wider">
                        {CATEGORY_LABELS[v.category] || v.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-bold text-white text-sm group-hover:text-red-500 transition-colors">{v.vehicle}</p>
                        {v.note && <p className="text-[10px] text-gray-500 mt-1 italic">{v.note}</p>}
                      </div>
                    </div>
                    {v.description && (
                      <p className="text-[11px] text-gray-500 leading-relaxed mb-3 line-clamp-2">{v.description}</p>
                    )}
                    <div className="flex items-end justify-between">
                      <div className="flex items-baseline gap-1">
                        <p className="text-xl font-black text-white">${v.retail_rate}</p>
                        <p className="text-[10px] text-gray-500">/day · by request</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/rentals/request?vehicle=${v.rental_id}`); }}
                        className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-full transition-colors"
                        data-testid={`request-btn-${v.rental_id}`}
                      >
                        Request <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Requirements */}
      <section className="py-12 border-t border-gray-900">
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => setShowRequirements(!showRequirements)}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-800 bg-gray-950 hover:border-gray-700 transition-all" data-testid="requirements-toggle">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-red-500" />
              <span className="font-bold text-sm">Renter Requirements & Booking Process</span>
            </div>
            {showRequirements ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
          </button>
          {showRequirements && (
            <div className="mt-4 p-6 rounded-xl border border-gray-800 bg-gray-950 space-y-6">
              <div>
                <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider mb-3">Requirements</h4>
                <ul className="space-y-2">
                  {['Age 25+ (Government-issued ID required)', 'Active full coverage insurance on your own policy', 'Insurance declarations page on file', 'Valid credit card — no debit', 'Security deposit required (varies by vehicle)', 'All renters are verified and screened'].map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" /> {r}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider mb-3">Booking Process</h4>
                <ol className="space-y-2">
                  {['Contact ACE to confirm vehicle availability', 'Provide ID and insurance declarations', 'Submit booking request with dates and vehicle', 'Security deposit collected via credit card', 'Vehicle prep and handoff at scheduled time', 'Return vehicle — deposit released after inspection'].map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="text-red-500 font-bold text-xs mt-0.5 shrink-0">{i + 1}.</span> {s}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 border-t border-gray-900 text-center">
        <div className="max-w-lg mx-auto px-6">
          <Zap className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Ready to Drive Something Extraordinary?</h3>
          <p className="text-sm text-gray-500 mb-6">Contact ACE to check availability, confirm pricing, and reserve your vehicle.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-11 px-6 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
            <Button onClick={() => navigate('/driving')} variant="outline" className="h-11 px-6 border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold">
              Explore Consulting Routes <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Rentals;
